import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { VALIDATION } from '../../constants';
import Logo from '../../components/common/Logo';

// Validation schema
const loginSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(VALIDATION.PHONE.PATTERN, VALIDATION.PHONE.MESSAGE),
  pin: yup
    .string()
    .required('PIN is required')
    .matches(VALIDATION.PIN.PATTERN, VALIDATION.PIN.MESSAGE),
});

interface LoginFormData {
  phone: string;
  pin: string;
}

const LoginPage: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  // Check for error message from navigation state
  React.useEffect(() => {
    const state = location.state as { error?: string } | null;
    if (state?.error) {
      setError(state.error);
    }
  }, [location]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setError('');
      await login(data.phone, data.pin);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Logo Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Logo size="large" color="#ffffff" />
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#ffffff', 
                mt: 2, 
                fontWeight: 300,
                letterSpacing: '0.5px'
              }}
            >
              Welcome Back
            </Typography>
          </Box>

          {/* Login Form */}
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 4,
              borderRadius: 3,
              backgroundColor: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography component="h1" variant="h4" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
                Sign In
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Enter your credentials to access the admin panel
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                {...register('phone')}
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                autoComplete="tel"
                autoFocus
                error={!!errors.phone}
                helperText={errors.phone?.message}
                placeholder="9999999999"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                {...register('pin')}
                margin="normal"
                required
                fullWidth
                name="pin"
                label="PIN"
                type="password"
                id="pin"
                autoComplete="current-password"
                error={!!errors.pin}
                helperText={errors.pin?.message}
                placeholder="1234"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                  },
                  boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
            {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                Default Super Admin Credentials:
              </Typography>
              <Typography variant="body2" sx={{ color: '#081122', fontWeight: 500 }}>
                Phone: 9999999999 | PIN: 1234
              </Typography>
            </Box> */}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
